import { create } from 'zustand';

const matchesConfiguredItem = (item, menuItemId, modifierSelections = [], baseComponents = [], portionId = null) => (
  item.menu_item_id === menuItemId &&
  String(item.portion_id || '') === String(portionId || '') &&
  JSON.stringify(item.modifier_selections || []) === JSON.stringify(modifierSelections || []) &&
  JSON.stringify(item.base_components || []) === JSON.stringify(baseComponents || [])
);

const useCartStore = create((set, get) => ({
  items: [],
  discountPercent: 0,
  discountAmount: 0,
  notes: '',

  addItem: (menuItem, modifierSelections = [], modifierPriceAdjustment = 0, baseComponents = [], portionData = null, recipeIngredientsSnapshot = [], itemQuantity = 1) => {
    const { items } = get();
    const portionId = portionData?.id || null;
    const existingItem = items.find(item => 
      matchesConfiguredItem(item, menuItem.id, modifierSelections, baseComponents, portionId)
    );

    const basePrice = parseFloat(portionData?.price ?? menuItem.selling_price ?? menuItem.price ?? 0);
    const totalPrice = basePrice + modifierPriceAdjustment;

    if (existingItem) {
      set({
        items: items.map(item =>
          matchesConfiguredItem(item, menuItem.id, modifierSelections, baseComponents, portionId)
            ? { ...item, quantity: item.quantity + itemQuantity }
            : item
        ),
      });
    } else {
      set({
        items: [
          ...items,
          {
            menu_item_id: menuItem.id,
            name: menuItem.name,
            portion_id: portionId,
            portion_name: portionData?.name || '',
            serving_type_name: portionData?.serving_type_name || '',
            base_price: basePrice,
            price: totalPrice,
            quantity: itemQuantity,
            notes: '',
            modifier_selections: modifierSelections,
            base_components: baseComponents,
            recipe_ingredients_snapshot: recipeIngredientsSnapshot,
          },
        ],
      });
    }
  },

  removeItem: (menuItemId, modifierSelections = [], baseComponents = [], portionId = null) => {
    set({
      items: get().items.filter(item => !matchesConfiguredItem(item, menuItemId, modifierSelections, baseComponents, portionId)),
    });
  },

  updateQuantity: (menuItemId, quantity, modifierSelections = [], baseComponents = [], portionId = null) => {
    if (quantity <= 0) {
      get().removeItem(menuItemId, modifierSelections, baseComponents, portionId);
      return;
    }

    set({
      items: get().items.map(item =>
        matchesConfiguredItem(item, menuItemId, modifierSelections, baseComponents, portionId)
          ? { ...item, quantity }
          : item
      ),
    });
  },

  updateItemNotes: (menuItemId, notes, modifierSelections = [], portionId = null) => {
    set({
      items: get().items.map(item =>
        item.menu_item_id === menuItemId &&
        String(item.portion_id || '') === String(portionId || '') &&
        JSON.stringify(item.modifier_selections || []) === JSON.stringify(modifierSelections || [])
          ? { ...item, notes }
          : item
      ),
    });
  },

  updateItemModifiers: (menuItemId, oldSelections, newSelections, newPrice, newBaseComponents = [], oldBaseComponents = []) => {
    set({
      items: get().items.map(item =>
        matchesConfiguredItem(item, menuItemId, oldSelections || [], oldBaseComponents || [], item.portion_id || null)
          ? { ...item, modifier_selections: newSelections, base_components: newBaseComponents, price: newPrice }
          : item
      ),
    });
  },

  setDiscount: (percent, amount) => {
    set({
      discountPercent: percent || 0,
      discountAmount: amount || 0,
    });
  },

  setNotes: (notes) => {
    set({ notes });
  },

  getSubtotal: () => {
    return get().items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  },

  getDiscountValue: () => {
    const subtotal = get().getSubtotal();
    const { discountPercent, discountAmount } = get();
    
    if (discountPercent > 0) {
      return (subtotal * discountPercent) / 100;
    }
    return discountAmount;
  },

  getTotal: () => {
    return get().getSubtotal() - get().getDiscountValue();
  },

  getItemCount: () => {
    return get().items.reduce((sum, item) => sum + item.quantity, 0);
  },

  clearCart: () => {
    set({
      items: [],
      discountPercent: 0,
      discountAmount: 0,
      notes: '',
    });
  },

  getOrderData: (paymentMethod, amountReceived, payments = []) => {
    const { items, discountPercent, discountAmount, notes } = get();
    
    return {
      items: items.map(item => ({
        menu_item_id: item.menu_item_id,
        menu_item_portion_id: item.portion_id || null,
        portion_name: item.portion_name || '',
        quantity: item.quantity,
        notes: item.notes,
        unit_price: item.price,
        recipe_ingredients_snapshot: item.recipe_ingredients_snapshot || [],
        base_components_snapshot: item.base_components || [],
        modifier_selections: (item.modifier_selections || []).map(selection => ({
          modifier_option_id: selection.modifier_option_id,
          quantity: selection.quantity,
          quantity_used: selection.quantity_used,
          price_at_time: selection.price_adjustment,
        })),
      })),
      payment_method: paymentMethod,
      amount_received: amountReceived,
      discount_percent: discountPercent,
      discount_amount: discountAmount,
      notes,
      payments,
    };
  },

  getItemDisplayName: (item) => {
    const baseName = item.portion_name ? `${item.name} - ${item.portion_name}` : item.name;
    if (!item.modifier_selections || item.modifier_selections.length === 0) {
      return baseName;
    }
    const modifierNames = item.modifier_selections.map(selection => selection.name).join(', ');
    return `${baseName} (${modifierNames})`;
  },
}));

export default useCartStore;
